import { Layout } from 'react-grid-layout'
import { UserDashboardLayoutDto, WidgetDto } from '@/api/ClientArea/Dashboards'

import { DashboardLayout, Widget, WidgetType } from './types'

export function normalizeLayouts(layouts: Layout[]) {
  if (layouts.length === 0) return layouts

  const minY = Math.min(...layouts.map((l) => l.y))

  return layouts.map((l) => ({
    ...l,
    y: l.y - minY,
  }))
}
export function buildThreePerRowLayout(widgets: Widget[]): Layout[] {
  const cols = 12
  let currentX = 0
  let currentY = 0
  let rowHeight = 0

  return widgets.map((widget) => {
    const w = widget.layout?.w ?? 4
    const h = widget.layout?.h ?? 4

    if (currentX + w > cols) {
      currentX = 0
      currentY += rowHeight
      rowHeight = 0
    }

    const layout = {
      i: widget.id,
      x: currentX,
      y: currentY,
      w,
      h,
      minW: widget.layout?.minW ?? w,
      minH: widget.layout?.minH ?? h,
    }

    currentX += w
    rowHeight = Math.max(rowHeight, h)

    return layout
  })
}

export function flattenWidgets(widgets: WidgetDto[]): WidgetDto[] {
  const flattened: WidgetDto[] = []

  for (const widget of widgets) {
    if (widget.hasSubWidgets && widget.subWidgets && widget.subWidgets.length > 0) {
      const usedSubWidgets = widget.subWidgets
      flattened.push(...flattenWidgets(usedSubWidgets))
    } else {
      flattened.push(widget)
    }
  }

  return flattened
}
export function mapWidgetDtoToWidget(dto: WidgetDto): Widget {
  return {
    id: dto.id,
    widgetId: dto.id,
    type: dto.type as WidgetType,
    size: dto.size,
    title: dto.name,
    layout: {
      i: dto.id,
      x: dto.layout?.x ?? 0,
      y: dto.layout?.y ?? 0,
      w: dto.layout?.w ?? dto.size.defaultWidth,
      h: dto.layout?.h ?? dto.size.defaultHeight,
      minW: dto.size.minWidth,
      minH: dto.size.minHeight,
      maxW: dto.size.maxWidth,
      maxH: dto.size.maxHeight,
    },
  }
}

export function mapLayoutDtoToLayout(dto: UserDashboardLayoutDto): DashboardLayout {
  if (!dto) {
    return {
      id: undefined as any,
      name: undefined as any,
      description: undefined as any,
      isPreset: false,
      widgets: [],
    }
  }

  const widgets = (dto.widgets || []).map(mapWidgetDtoToWidget)

  const normalizedWidgets = dto.isPreset
    ? widgets.map((widget, index) => {
        const col = index % 2
        const row = Math.floor(index / 2)

        return {
          ...widget,
          layout: {
            ...widget.layout,
            x: col * widget.size.defaultWidth,
            y: row * widget.size.defaultHeight,
            w: widget.layout?.w ?? widget.size.defaultWidth,
            h: widget.layout?.h ?? widget.size.defaultHeight,
          },
        }
      })
    : widgets
  return {
    id: dto.id,
    name: dto.name,
    description: dto.description,
    isPreset: dto.isPreset,
    widgets: normalizedWidgets,
  }
}
export function serializeWidgetOptions(widgets: Widget[], rglLayouts: Layout[]) {
  return widgets.map((widget) => {
    const layout = rglLayouts.find((l) => l.i === widget.id)

    return {
      id: widget.widgetId,
      layout: {
        x: layout?.x ?? widget.layout.x,
        y: layout?.y ?? widget.layout.y,
        w: layout?.w ?? widget.layout.w,
        h: layout?.h ?? widget.layout.h,
      },
      mobileLayout: null,
    }
  })
}
interface ValidateLayoutParams {
  name: string
  description: string
  titleLimit: number
  descriptionLimit: number
  nameRegex: RegExp
}

interface ValidateLayoutResult {
  isValid: boolean
  error?: string
  trimmedName: string
  trimmedDescription: string
}

export const validateLayout = ({
  name,
  description,
  titleLimit,
  descriptionLimit,
  nameRegex,
}: ValidateLayoutParams): ValidateLayoutResult => {
  const trimmedName = name.trim()
  const trimmedDescription = description.trim()

  if (!trimmedName) {
    return { isValid: false, error: 'Please enter a layout name', trimmedName, trimmedDescription }
  }

  if (trimmedName.length > titleLimit) {
    return {
      isValid: false,
      error: `Layout name must be ${titleLimit} characters or less`,
      trimmedName,
      trimmedDescription,
    }
  }

  if (trimmedDescription.length > descriptionLimit) {
    return {
      isValid: false,
      error: `Description must be ${descriptionLimit} characters or less`,
      trimmedName,
      trimmedDescription,
    }
  }

  if (!nameRegex.test(trimmedName)) {
    return {
      isValid: false,
      error: 'Layout name can only contain English letters, numbers, spaces and hyphens (-)',
      trimmedName,
      trimmedDescription,
    }
  }

  return {
    isValid: true,
    trimmedName: trimmedName.slice(0, titleLimit),
    trimmedDescription: trimmedDescription.slice(0, descriptionLimit),
  }
}
